import React, { useCallback, useEffect, useState, createContext, useContext, useRef } from "react";
import { Segment, GlossaryEntry, Document, SegmentStatus, mockGlossary } from "../data/mockData";
import { fetchTemplates, fetchTemplateBodies, groupIntoPairs, DocumentPair } from "../data/api";
import { parseLexicalBody, matchTranslations, segmentsToInjectionTexts } from "../data/lexicalParser";
import { getStoredApiKey, setStoredApiKey, ModelConfig, DEFAULT_MODELS } from "../data/translationService";
import { StoredSegmentState, SyncReport, syncSegmentsWithStored, storedStatesFromLegacyOverrides } from "../data/segmentSync";
import { hashSourceText } from "../data/contentHash";
import { fetchWorkspaceSettings, saveWorkspaceSettings, fetchAllDocumentStates, fetchDocumentState, upsertDocumentState, DocumentStateRecord } from "../data/translationStateApi";
const GLOSSARY_STORAGE_KEY = "lawvo_glossary";
const MODELS_STORAGE_KEY = "lawvo_model_configs";
const SEGMENT_OVERRIDES_KEY = "lawvo_segment_overrides";
const DRAFT_CACHE_KEY = "lawvo_draft_cache";
const MIGRATION_FLAG_KEY = "lawvo_state_migrated_to_server";
function loadLocalGlossary(): GlossaryEntry[] {
  try {
    const stored = localStorage.getItem(GLOSSARY_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {
    // ignore
  }
  return mockGlossary;
}
function loadLocalModelConfigs(): ModelConfig[] {
  try {
    const stored = localStorage.getItem(MODELS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length === 3) return parsed;
    }
  } catch {
    // ignore
  }
  return DEFAULT_MODELS;
}
function loadLegacyOverrides(): Record<string, {
  status: SegmentStatus;
  mergedText?: string;
  sourceText?: string;
}> {
  try {
    const stored = localStorage.getItem(SEGMENT_OVERRIDES_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    // ignore
  }
  return {};
}
export interface DocSyncStats extends SyncReport {
  matched: number;
  edited: number;
  added: number;
  removed: number;
}
function reportToDocStats(report: SyncReport): DocSyncStats {
  return {
    ...report,
    matched: report.unchanged,
    edited: report.changedSections,
    added: report.newSections,
    removed: report.removedSections
  };
}
interface TranslationContextType {
  documents: Document[];
  currentDocument: Document | undefined;
  setCurrentDocumentId: (id: string) => void;
  segments: Segment[];
  glossary: GlossaryEntry[];
  updateSegmentStatus: (id: string, status: SegmentStatus, mergedText?: string, options?: {
    lock?: boolean;
  }) => void;
  unlockSegment: (id: string) => void;
  splitSection: (id: string, splitAt: number) => void;
  addGlossaryEntry: (entry: Omit<GlossaryEntry, "id">) => void;
  updateGlossaryEntry: (id: string, entry: Partial<GlossaryEntry>) => void;
  deleteGlossaryEntry: (id: string) => void;
  documentTitle: string;
  addDocument: (title: string, sourceText: string) => string;
  isLoading: boolean;
  error: string | null;
  refreshDocuments: () => Promise<Map<string, DocSyncStats>>;
  syncDocumentFromServer: (docId: string) => Promise<DocSyncStats | null>;
  lastSyncReport: SyncReport | null;
  openRouterApiKey: string;
  setOpenRouterApiKey: (key: string) => void;
  modelConfigs: ModelConfig[];
  setModelConfigs: (configs: ModelConfig[]) => void;
  setGlossaryFromBackup: (entries: GlossaryEntry[]) => void;
  segmentOverrides: Record<string, StoredSegmentState>;
  setSegmentOverridesFromBackup: (overrides: Record<string, {
    status: SegmentStatus;
    mergedText?: string;
    sourceText?: string;
  }>) => void;
  restoreDocumentStatesFromBackup: (states: Record<string, StoredSegmentState[]>) => void;
  clearAllTranslations: () => void;
  documentStates: Record<string, StoredSegmentState[]>;
  persistDocumentState: (docId: string) => Promise<void>;
}
const TranslationContext = createContext<TranslationContextType | undefined>(undefined);
function pairToBaseDocument(pair: DocumentPair): Document {
  const enBody = pair.enTemplate.body;
  const frBody = pair.frTemplate?.body || null;
  const enSegments = parseLexicalBody(enBody);
  const frMatches = matchTranslations(enSegments, frBody);
  const translatable = enSegments.filter(s => s.isTranslatable);
  const segments: Segment[] = translatable.map((seg, index) => ({
    id: `pending-${index}`,
    sectionId: `pending-${index}`,
    structuralPath: seg.structuralPath,
    sourceHash: hashSourceText(seg.text),
    clauseId: seg.type === "heading" ? seg.text : `Section ${index + 1}`,
    sourceText: seg.text,
    status: "not_started" as const,
    existingTranslation: frMatches[index],
    injectionIndex: seg.injectionIndex
  }));
  return {
    id: pair.id,
    title: pair.title,
    createdAt: pair.createdAt,
    sourceLanguage: pair.enTemplate.locale === "fr" ? "FR" : "EN",
    targetLanguage: pair.enTemplate.locale === "fr" ? "EN" : "FR",
    segments,
    status: "translating",
    isActive: pair.enTemplate.active,
    enBody,
    frBody,
    templateId: pair.enTemplate.id,
    frTemplateId: pair.frTemplate?.id || null,
    locales: [...(pair.enTemplate.locale === "en" ? ["EN"] : []), ...(pair.enTemplate.locale === "fr" ? ["FR"] : []), ...(pair.frTemplate ? ["FR"] : [])].filter((v, i, a) => a.indexOf(v) === i)
  };
}
function deriveDocumentStatus(segments: Segment[]): Document["status"] {
  const active = segments.filter(s => s.status !== "removed");
  const approvedCount = active.filter(s => s.status === "approved").length;
  if (approvedCount === active.length && active.length > 0) return "completed";
  if (approvedCount === 0 && !active.some(s => s.status === "in_progress")) {
    return "translating";
  }
  return "in_review";
}
export const TranslationProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [currentDocumentId, setCurrentDocumentId] = useState<string | null>(null);
  const [glossary, setGlossary] = useState<GlossaryEntry[]>(loadLocalGlossary);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openRouterApiKey, setOpenRouterApiKeyState] = useState<string>(getStoredApiKey());
  const [modelConfigs, setModelConfigsState] = useState<ModelConfig[]>(loadLocalModelConfigs);
  const [documentStates, setDocumentStates] = useState<Record<string, StoredSegmentState[]>>({});
  const [draftCaches, setDraftCaches] = useState<Record<string, Record<string, unknown>>>({});
  const [lastSyncReport, setLastSyncReport] = useState<SyncReport | null>(null);
  const stateRecordsRef = useRef<Record<string, DocumentStateRecord>>({});
  const saveWorkspaceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistWorkspaceSettings = useCallback(async (g: GlossaryEntry[], models: ModelConfig[]) => {
    try {
      await saveWorkspaceSettings({
        glossary: g,
        modelConfigs: models
      });
    } catch (err) {
      console.warn("Failed to save workspace settings to server:", err);
      localStorage.setItem(GLOSSARY_STORAGE_KEY, JSON.stringify(g));
      localStorage.setItem(MODELS_STORAGE_KEY, JSON.stringify(models));
    }
  }, []);
  const persistDocumentState = useCallback(async (docId: string) => {
    const doc = documents.find(d => d.id === docId);
    if (!doc?.templateId) return;
    const states = documentStates[docId] || [];
    const draftCache = draftCaches[docId] || {};
    try {
      const record = await upsertDocumentState({
        documentPairKey: docId,
        enTemplateId: doc.templateId,
        frTemplateId: doc.frTemplateId,
        segments: states.filter(s => s.status !== "removed"),
        draftCache
      });
      stateRecordsRef.current[docId] = record;
    } catch (err) {
      console.warn(`Failed to persist document state for ${docId}:`, err);
    }
  }, [documents, documentStates, draftCaches]);
  const migrateLocalToServer = useCallback(async () => {
    if (localStorage.getItem(MIGRATION_FLAG_KEY) === "true") return;
    try {
      const localGlossary = loadLocalGlossary();
      const localModels = loadLocalModelConfigs();
      const legacyOverrides = loadLegacyOverrides();
      const existing = await fetchWorkspaceSettings();
      if (!existing?.glossary?.length && localGlossary.length) {
        await saveWorkspaceSettings({
          glossary: localGlossary,
          modelConfigs: localModels
        });
      }
      if (Object.keys(legacyOverrides).length > 0) {
        const allStates = await fetchAllDocumentStates();
        if (allStates.length === 0) {
          const templates = await fetchTemplates();
          const pairs = groupIntoPairs(templates);
          for (const pair of pairs) {
            const base = pairToBaseDocument(pair);
            const states = storedStatesFromLegacyOverrides(pair.id, base.segments, legacyOverrides);
            if (states.some(s => s.mergedText || s.status !== "not_started")) {
              await upsertDocumentState({
                documentPairKey: pair.id,
                enTemplateId: pair.enTemplate.id,
                frTemplateId: pair.frTemplate?.id,
                segments: states,
                draftCache: {}
              });
            }
          }
        }
      }
      localStorage.setItem(MIGRATION_FLAG_KEY, "true");
    } catch (err) {
      console.warn("Local-to-server migration skipped:", err);
    }
  }, []);
  const loadDocuments = useCallback(async (): Promise<Map<string, DocSyncStats>> => {
    setIsLoading(true);
    setError(null);
    const statsByDoc = new Map<string, DocSyncStats>();
    try {
      await migrateLocalToServer();
      const [templates, workspaceSettings, allDocStates] = await Promise.all([fetchTemplates(), fetchWorkspaceSettings().catch(() => null), fetchAllDocumentStates().catch(() => [] as DocumentStateRecord[])]);
      if (workspaceSettings?.glossary?.length) {
        setGlossary(workspaceSettings.glossary);
      }
      if (workspaceSettings?.modelConfigs?.length === 3) {
        setModelConfigsState(workspaceSettings.modelConfigs);
      }
      const statesByDoc: Record<string, StoredSegmentState[]> = {};
      const cachesByDoc: Record<string, Record<string, unknown>> = {};
      const recordsByDoc: Record<string, DocumentStateRecord> = {};
      for (const record of allDocStates) {
        statesByDoc[record.documentPairKey] = record.segments || [];
        cachesByDoc[record.documentPairKey] = record.draftCache || {};
        recordsByDoc[record.documentPairKey] = record;
      }
      stateRecordsRef.current = recordsByDoc;
      setDocumentStates(statesByDoc);
      setDraftCaches(cachesByDoc);
      const pairs = groupIntoPairs(templates);
      const docs = await Promise.all(pairs.map(async pair => {
        let enBody = pair.enTemplate.body;
        let frBody = pair.frTemplate?.body ?? null;
        if (!enBody?.trim()) {
          const fresh = await fetchTemplateBodies(pair.enTemplate.id, pair.frTemplate?.id);
          enBody = fresh.enBody;
          frBody = fresh.frBody;
        }
        const parsedEn = parseLexicalBody(enBody);
        const enSegments = parsedEn.filter(s => s.isTranslatable);
        const frMatches = matchTranslations(parsedEn, frBody);
        const stored = statesByDoc[pair.id] || [];
        const {
          segments,
          states,
          report
        } = syncSegmentsWithStored(enSegments, stored, frMatches);
        statsByDoc.set(pair.id, reportToDocStats(report));
        const activeStates = states.filter(s => s.status !== "removed");
        statesByDoc[pair.id] = activeStates;
        const base = pairToBaseDocument(pair);
        return {
          ...base,
          enBody,
          frBody,
          segments: segments.filter(s => s.status !== "removed"),
          status: deriveDocumentStatus(segments)
        };
      }));
      setDocumentStates(statesByDoc);
      setDocuments(docs);
    } catch (err) {
      console.error("Failed to fetch templates:", err);
      setError(err instanceof Error ? err.message : "Failed to load documents");
    } finally {
      setIsLoading(false);
    }
    return statsByDoc;
  }, [migrateLocalToServer]);
  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);
  useEffect(() => {
    if (saveWorkspaceTimer.current) clearTimeout(saveWorkspaceTimer.current);
    saveWorkspaceTimer.current = setTimeout(() => {
      persistWorkspaceSettings(glossary, modelConfigs);
    }, 800);
    return () => {
      if (saveWorkspaceTimer.current) clearTimeout(saveWorkspaceTimer.current);
    };
  }, [glossary, modelConfigs, persistWorkspaceSettings]);
  const handleSetApiKey = (key: string) => {
    setOpenRouterApiKeyState(key);
    setStoredApiKey(key);
  };
  const handleSetModelConfigs = (configs: ModelConfig[]) => {
    setModelConfigsState(configs);
  };
  const currentDocument = documents.find(d => d.id === currentDocumentId);
  const segments = currentDocument?.segments || [];
  const documentTitle = currentDocument?.title || "";
  const updateSegmentStatus = (id: string, status: SegmentStatus, mergedText?: string, options?: {
    lock?: boolean;
  }) => {
    if (!currentDocumentId) return;
    const docId = currentDocumentId;
    const currentSeg = documents.find(d => d.id === docId)?.segments.find(s => s.id === id);
    const locked = options?.lock === true ? true : options?.lock === false ? false : status === "approved" ? true : currentSeg?.locked;
    setDocuments(prev => prev.map(doc => {
      if (doc.id !== docId) return doc;
      const updatedSegments = doc.segments.map(seg => seg.id === id ? {
        ...seg,
        status,
        mergedText: mergedText ?? seg.mergedText,
        locked
      } : seg);
      return {
        ...doc,
        segments: updatedSegments,
        status: deriveDocumentStatus(updatedSegments)
      };
    }));
    setDocumentStates(prev => {
      const existing = prev[docId] || [];
      const updated = existing.map(s => s.sectionId === id ? {
        ...s,
        status,
        mergedText: mergedText ?? s.mergedText,
        sourceText: currentSeg?.sourceText ?? s.sourceText,
        sourceHash: currentSeg?.sourceHash ?? s.sourceHash,
        locked: locked ?? s.locked
      } : s);
      const next = {
        ...prev,
        [docId]: updated
      };
      queueMicrotask(() => persistDocumentState(docId));
      return next;
    });
  };
  const unlockSegment = (id: string) => {
    updateSegmentStatus(id, "in_progress", undefined, {
      lock: false
    });
  };
  const splitSection = (id: string, splitAt: number) => {
    if (!currentDocumentId) return;
    const docId = currentDocumentId;
    const seg = documents.find(d => d.id === docId)?.segments.find(s => s.id === id);
    if (!seg || seg.locked) return;
    if (splitAt <= 0 || splitAt >= seg.sourceText.length) return;
    const partA = seg.sourceText.slice(0, splitAt).trim();
    const partB = seg.sourceText.slice(splitAt).trim();
    if (!partA || !partB) return;
    setDocuments(prev => prev.map(doc => {
      if (doc.id !== docId) return doc;
      const idx = doc.segments.findIndex(s => s.id === id);
      if (idx < 0) return doc;
      const segA: Segment = {
        ...seg,
        sourceText: partA,
        sourceHash: hashSourceText(partA),
        status: "in_progress",
        mergedText: undefined,
        locked: false
      };
      const segB: Segment = {
        ...seg,
        id: `${seg.sectionId}-split-b`,
        sectionId: `${seg.sectionId}-split-b`,
        sourceText: partB,
        sourceHash: hashSourceText(partB),
        status: "not_started",
        mergedText: undefined,
        locked: false
      };
      const updated = [...doc.segments.slice(0, idx), segA, segB, ...doc.segments.slice(idx + 1)];
      return {
        ...doc,
        segments: updated
      };
    }));
  };
  const syncDocumentFromServer = useCallback(async (docId: string): Promise<DocSyncStats | null> => {
    const doc = documents.find(d => d.id === docId);
    if (!doc?.templateId) {
      throw new Error("Document not found or missing English template.");
    }
    const [freshBodies, storedRecord] = await Promise.all([fetchTemplateBodies(doc.templateId, doc.frTemplateId), fetchDocumentState(docId).catch(() => null)]);
    const stored = storedRecord?.segments || documentStates[docId] || [];
    const draftCache = storedRecord?.draftCache || draftCaches[docId] || {};
    const parsedEn = parseLexicalBody(freshBodies.enBody);
    const enSegments = parsedEn.filter(s => s.isTranslatable);
    const frMatches = matchTranslations(parsedEn, freshBodies.frBody);
    const {
      segments,
      states,
      report
    } = syncSegmentsWithStored(enSegments, stored, frMatches);
    const activeStates = states.filter(s => s.status !== "removed");
    const activeSegments = segments.filter(s => s.status !== "removed");
    const stats = reportToDocStats(report);
    setDocumentStates(prev => ({
      ...prev,
      [docId]: activeStates
    }));
    setDocuments(prev => prev.map(d => d.id === docId ? {
      ...d,
      enBody: freshBodies.enBody,
      frBody: freshBodies.frBody,
      segments: activeSegments,
      status: deriveDocumentStatus(segments)
    } : d));
    try {
      const record = await upsertDocumentState({
        documentPairKey: docId,
        enTemplateId: doc.templateId,
        frTemplateId: doc.frTemplateId,
        segments: activeStates,
        draftCache
      });
      stateRecordsRef.current[docId] = record;
    } catch (err) {
      console.warn(`Failed to persist sync results for ${docId}:`, err);
    }
    setLastSyncReport(report);
    return stats;
  }, [documents, documentStates, draftCaches]);
  const refreshDocuments = useCallback(async () => {
    const statsMap = await loadDocuments();
    if (currentDocumentId) {
      const report = statsMap.get(currentDocumentId);
      if (report) setLastSyncReport(report);
    }
    return statsMap;
  }, [loadDocuments, currentDocumentId]);
  const addDocument = (title: string, sourceText: string) => {
    const rawSegments = sourceText.split(/\n\s*\n/).filter(text => text.trim().length > 0);
    const newSegments: Segment[] = rawSegments.map((text, index) => ({
      id: `seg-new-${Date.now()}-${index}`,
      sectionId: `seg-new-${Date.now()}-${index}`,
      structuralPath: `manual-${index}`,
      sourceHash: hashSourceText(text.trim()),
      clauseId: `Section ${index + 1}`,
      sourceText: text.trim(),
      status: "in_progress"
    }));
    const newDoc: Document = {
      id: `doc-${Date.now()}`,
      title,
      createdAt: new Date().toISOString(),
      sourceLanguage: "EN",
      targetLanguage: "FR",
      segments: newSegments,
      status: "in_review",
      isActive: true
    };
    setDocuments(prev => [newDoc, ...prev]);
    return newDoc.id;
  };
  const addGlossaryEntry = (entry: Omit<GlossaryEntry, "id">) => {
    setGlossary(prev => [...prev, {
      ...entry,
      id: `g${Date.now()}`
    }]);
  };
  const updateGlossaryEntry = (id: string, entry: Partial<GlossaryEntry>) => {
    setGlossary(prev => prev.map(g => g.id === id ? {
      ...g,
      ...entry
    } : g));
  };
  const deleteGlossaryEntry = (id: string) => {
    setGlossary(prev => prev.filter(g => g.id !== id));
  };
  const setGlossaryFromBackup = (entries: GlossaryEntry[]) => {
    setGlossary(entries);
  };
  const setSegmentOverridesFromBackup = (overrides: Record<string, {
    status: SegmentStatus;
    mergedText?: string;
    sourceText?: string;
  }>) => {
    const byDoc: Record<string, StoredSegmentState[]> = {};
    for (const doc of documents) {
      byDoc[doc.id] = storedStatesFromLegacyOverrides(doc.id, doc.segments, overrides);
    }
    setDocumentStates(prev => ({
      ...prev,
      ...byDoc
    }));
    for (const docId of Object.keys(byDoc)) {
      persistDocumentState(docId);
    }
    loadDocuments();
  };
  const restoreDocumentStatesFromBackup = (states: Record<string, StoredSegmentState[]>) => {
    setDocumentStates(prev => ({
      ...prev,
      ...states
    }));
    for (const docId of Object.keys(states)) {
      persistDocumentState(docId);
    }
    loadDocuments();
  };
  const clearAllTranslations = async () => {
    setDocumentStates({});
    setDraftCaches({});
    localStorage.removeItem(DRAFT_CACHE_KEY);
    localStorage.removeItem(SEGMENT_OVERRIDES_KEY);
    for (const doc of documents) {
      if (doc.templateId) {
        await upsertDocumentState({
          documentPairKey: doc.id,
          enTemplateId: doc.templateId,
          frTemplateId: doc.frTemplateId,
          segments: [],
          draftCache: {}
        });
      }
    }
    loadDocuments();
  };
  const segmentOverridesFlat: Record<string, StoredSegmentState> = {};
  for (const [docId, states] of Object.entries(documentStates)) {
    for (const s of states) {
      segmentOverridesFlat[`${docId}-${s.sectionId}`] = s;
    }
  }
  return <TranslationContext.Provider value={{
    documents,
    currentDocument,
    setCurrentDocumentId,
    segments,
    glossary,
    updateSegmentStatus,
    unlockSegment,
    splitSection,
    addGlossaryEntry,
    updateGlossaryEntry,
    deleteGlossaryEntry,
    documentTitle,
    addDocument,
    isLoading,
    error,
    refreshDocuments,
    syncDocumentFromServer,
    lastSyncReport,
    openRouterApiKey,
    setOpenRouterApiKey: handleSetApiKey,
    modelConfigs,
    setModelConfigs: handleSetModelConfigs,
    setGlossaryFromBackup,
    segmentOverrides: segmentOverridesFlat,
    setSegmentOverridesFromBackup,
    restoreDocumentStatesFromBackup,
    clearAllTranslations,
    documentStates,
    persistDocumentState
  }} data-id="element-242">
      {children}
    </TranslationContext.Provider>;
};
export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error("useTranslation must be used within a TranslationProvider");
  }
  return context;
};

// Re-export for injection helper used by hub/workspace pages
export { segmentsToInjectionTexts };